import _ from 'lodash'
import { getPartnerAvailableRiderByDisplayProductCode } from 'quick-quote/selectors/partner-selector'
import { call, fork, put, select } from 'redux-saga/effects'

import VALUES from 'core/data-model/constants/values'
import { partnerRequesting } from 'quick-quote/actions'
import { getAge } from 'quick-quote/insured-information/selectors'
import {
  editRiderSelectedPlan,
  editSumAssured,
  updateAvailableRiders,
  updateBasicPremiumState,
  updateSelectBasicPlan,
} from 'quick-quote/product-common/coverage-plan/actions'
import { getSumAssured } from 'quick-quote/product-common/coverage-plan/selectors'
import * as productCommonCoverageSelector from 'quick-quote/product-common/coverage-plan/selectors'
import { editCoveragePeriod, editInterestRate, editLoanAmount, editLoanPeriod } from 'quick-quote/product-mrta/actions'
import {
  getCoveragePeriod,
  getLoanPeriod,
  getMRTATRUNCATELoanPeriod,
} from 'quick-quote/product-mrta/coverage-plan/selectors'
import * as productMRTACoverageSelector from 'quick-quote/product-mrta/coverage-plan/selectors'
import { getRiderPlanName } from 'quick-quote/product-mrta/sagas'
import { getSelectedDisplayProduct } from 'quick-quote/product-selection/selectors'

/**
 * createProductMRTACoveragePlanService
 * @param productCommonService {ReturnType<import('core/service-wrapper/partner-api/handler/product-common').createProductCommonService>}
 */
export function createProductMRTACoveragePlanService(productCommonService) {
  function* selectProductMRTA(action) {
    const basicPlanCode = action.payload

    const loanPeriod = yield select(productMRTACoverageSelector.getLoanPeriod)
    const loanAmount = yield select(productMRTACoverageSelector.getLoanAmount)
    const sumAssured = yield select(productCommonCoverageSelector.getSumAssured)
    const coveragePeriod = yield select(productMRTACoverageSelector.getCoveragePeriod)

    const basicPlans = productCommonService.getDisplayProductFromCache(basicPlanCode)
    let basicPlan = basicPlans.find((plan) => plan.coveragePeriod.value === loanPeriod)
    if (_.isUndefined(basicPlan)) {
      basicPlan = basicPlans[0]
    }

    const availableRiders = yield call(productCommonService.getAvailableRiderByBasicPlanCode, basicPlan)

    yield put(updateSelectBasicPlan(basicPlan))
    yield put(updateAvailableRiders(availableRiders))
    yield put(editSumAssured(sumAssured))
    yield put(updateBasicPremiumState(-1))
    yield put(editLoanAmount(loanAmount))

    // default loan period
    const loanPeriodOptions = yield select(productMRTACoverageSelector.getOptionsLoanPeriod)
    const findLoanPeriod = loanPeriodOptions.find((option) => option.value === Number(loanPeriod))
    if (findLoanPeriod) {
      yield put(editLoanPeriod(loanPeriod))
    } else {
      yield put(editLoanPeriod(loanPeriodOptions[0].value))
    }

    // default coverage period
    const coveragePeriodOptions = yield select(productMRTACoverageSelector.getOptionsCoveragePeriod)
    const findCoveragePeriod = coveragePeriodOptions.find((option) => option.value === Number(coveragePeriod))
    if (findCoveragePeriod) {
      yield put(editCoveragePeriod(coveragePeriod))
    } else {
      yield put(editCoveragePeriod(coveragePeriodOptions[0].value))
    }
  }

  // clone from product-mrta/coverage-plan/sagas
  const filterBasicPlanCodeOptionMRTA = (code, loanPeriod, coveragePeriod, age) => {
    switch (code) {
      case VALUES.MRTA_CODE.MRTA_PLUS:
      case VALUES.MRTA_CODE.MRTA_FULL:
      case VALUES.GLTSP_CODE.GLTSP:
        const products = productCommonService.getDisplayProductFromCache(code, { listAll: true })
        return products.find((option) => option.coveragePeriod.value === Number(coveragePeriod))
      case VALUES.MRTA_CODE.MRTA_TRUNCATE: {
        const loanPeriodMRTATRUNCATE = getMRTATRUNCATELoanPeriod(loanPeriod, age)
        const products = productCommonService.getDisplayProductFromCache(code, { listAll: true })
        return products.find((option) => option.coveragePeriod.value === Number(loanPeriodMRTATRUNCATE))
      }
      default:
        return undefined
    }
  }

  function* validateLoanPeriodChange(action) {
    const { value } = action.payload
    const loanPeriod = Number(value)

    const selectedDisplayProduct = yield select(getSelectedDisplayProduct)
    const coveragePeriod = yield select(getCoveragePeriod)
    const age = yield select(getAge)

    // update basicPlanCode
    const newBasicPlan = filterBasicPlanCodeOptionMRTA(selectedDisplayProduct.code, loanPeriod, coveragePeriod, age)
    if (newBasicPlan) {
      yield put(updateSelectBasicPlan(newBasicPlan))
    }

    yield put(partnerRequesting('change_loan_period'))
    yield fork(productCommonService.calculateWithValidation)
  }

  function* validateCoveragePeriodChange(action) {
    const coveragePeriod = action.payload

    const selectedDisplayProduct = yield select(getSelectedDisplayProduct)
    const loanPeriod = yield select(getLoanPeriod)
    const age = yield select(getAge)

    const newBasicPlan = filterBasicPlanCodeOptionMRTA(selectedDisplayProduct.code, loanPeriod, coveragePeriod, age)
    if (newBasicPlan && selectedDisplayProduct.basicPlanCode !== newBasicPlan.basicPlanCode) {
      yield put(updateSelectBasicPlan(newBasicPlan))
    }

    // update MRTAPP riderCode

    const availableRiders = yield select(getPartnerAvailableRiderByDisplayProductCode(selectedDisplayProduct.code))
    const riderCode = getRiderPlanName(selectedDisplayProduct.code, coveragePeriod)
    const rider = availableRiders[riderCode]
    if (rider) {
      yield put(editRiderSelectedPlan('MRTAAP', { planCode: riderCode }))
    }

    // yield put(productMRTA.updateCoveragePeriod(coveragePeriod))
    yield put(partnerRequesting('change_coverage_period'))
    yield fork(productCommonService.calculateWithValidation)
  }

  function* resetInterestRate() {
    const sumAssured = yield select(getSumAssured)
    yield put(editInterestRate(0))
    yield put(editSumAssured(sumAssured))
  }

  function* onEditLoanAmount(action) {
    // const { value } = action.payload
    yield put(partnerRequesting('change_loan_amount'))
    yield fork(productCommonService.calculateWithValidation)
  }

  function* validateInterestRateErrors(action) {
    yield put(partnerRequesting('validate_interest_rate'))
    yield fork(productCommonService.calculateWithValidation)
  }

  return {
    selectProductMRTA,
    validateLoanPeriodChange,
    validateCoveragePeriodChange,
    resetInterestRate,
    onEditLoanAmount,
    validateInterestRateErrors,
  }
}
